import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, Package, ShoppingBag, ArrowLeft, Tag } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { getBundle } from "@/lib/api";

export default function BundleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { addBundle, hasItem } = useCart();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getBundle(slug)
      .then(setBundle)
      .catch(() => navigate("/pakker"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading || !bundle) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-[#6B726C]">{t("loading")}</div>
    );
  }

  const title = lang === "en" ? bundle.title_en : bundle.title_no;
  const short = lang === "en" ? bundle.short_en : bundle.short_no;
  const desc = lang === "en" ? bundle.description_en : bundle.description_no;
  const inCart = hasItem(bundle.id, "bundle");

  return (
    <div className="bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-16">
        <Link
          to="/pakker"
          data-testid="back-to-bundles"
          className="inline-flex items-center gap-2 text-sm text-[#6B726C] hover:text-[#2C302E] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {lang === "en" ? "Back to bundles" : "Tilbake til pakker"}
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
          {/* Image */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-white border border-[#E5E2DC]">
              <img src={bundle.image_url} alt={title} className="w-full h-full object-cover" />
              <div className="absolute top-5 left-5 bg-[#D97B66] text-white rounded-full px-4 py-1.5 text-sm font-medium">
                −{bundle.savings_percent}% · {t("bundle_save")} {bundle.savings_nok} NOK
              </div>
            </div>
          </div>

          {/* Info */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium mb-4 inline-flex items-center gap-2">
              <Package className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t("bundles_eyebrow")} · {bundle.products?.length} {t("bundle_products")}
            </p>
            <h1 className="font-heading text-3xl md:text-5xl tracking-tight font-light text-[#2C302E] leading-tight">
              {title}
            </h1>
            <p className="mt-5 text-lg text-[#6B726C] leading-relaxed">{short}</p>

            {/* Price */}
            <div className="mt-8 flex items-end gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#6B726C]">
                  {t("bundle_value")} {bundle.original_price_nok} NOK
                </p>
                <p className="font-heading text-4xl md:text-5xl text-[#2C302E] font-medium mt-1">
                  {bundle.price_nok}{" "}
                  <span className="text-base text-[#6B726C] font-normal">NOK</span>
                </p>
              </div>
              <div className="bg-[#8BA896]/15 border border-[#8BA896]/30 text-[#7A9685] rounded-full px-3 py-1.5 text-xs font-medium mb-2 flex items-center gap-1">
                <Tag className="w-3 h-3" strokeWidth={2} />
                {t("bundle_save")} {bundle.savings_nok} NOK
              </div>
            </div>

            <button
              data-testid="add-bundle-button"
              onClick={() => addBundle(bundle)}
              disabled={inCart}
              className={`mt-8 w-full rounded-full px-8 py-4 font-medium transition-all flex items-center justify-center gap-3 ${
                inCart
                  ? "bg-[#8BA896] text-white cursor-default"
                  : "bg-[#D97B66] hover:bg-[#C66A55] text-white"
              }`}
            >
              {inCart ? (
                <>
                  <Check className="w-5 h-5" strokeWidth={2} />
                  {t("bundle_in_cart")}
                </>
              ) : (
                <>
                  <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                  {t("bundle_add_cart")}
                </>
              )}
            </button>

            <div className="mt-12 pt-8 border-t border-[#E5E2DC]">
              <h2 className="font-heading text-xl text-[#2C302E] font-medium mb-4">
                {t("product_about")}
              </h2>
              <p className="text-[#6B726C] leading-relaxed whitespace-pre-line">{desc}</p>
            </div>
          </div>
        </div>

        {/* Included Products */}
        <div className="mt-20 pt-16 border-t border-[#E5E2DC]">
          <h3 className="font-heading text-2xl md:text-3xl text-[#2C302E] font-light mb-10">
            {t("bundle_includes")} · {bundle.products?.length} {t("bundle_products")}
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {bundle.products?.map((p) => {
              const ptitle = lang === "en" ? p.title_en : p.title_no;
              const pshort = lang === "en" ? p.short_en : p.short_no;
              return (
                <Link
                  key={p.id}
                  to={`/produkt/${p.slug}`}
                  data-testid={`bundle-included-${p.slug}`}
                  className="group bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] transition-all duration-500"
                >
                  <div className="aspect-[5/4] overflow-hidden bg-[#F9F8F6]">
                    <img
                      src={p.image_url}
                      alt={ptitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <h4 className="font-heading text-base text-[#2C302E] font-medium leading-snug mb-1.5">
                      {ptitle}
                    </h4>
                    <p className="text-xs text-[#6B726C] line-clamp-2 mb-3">{pshort}</p>
                    <p className="text-sm text-[#6B726C] line-through">{p.price_nok} NOK</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
